import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { ReactComponent as TrashIcon } from '../../../../../assets/icons/v2/trash.svg';

import DialogModal from '../../../../../components/DialogModal';
import { SaveButton, CancelButton } from '../../../../../components/Button/ActionButtons';
import InputLabel from '../../../../../components/v2/InputLabel';
import { StyledTextField } from '../../../../../components/Inputs';
import texts from '../../texts.json';
import { ClearButton } from './style';

const t = texts.brevoSession.dialog;

const LOCAL_FORMAT = 'YYYY-MM-DDTHH:mm';

const toLocalInputValue = (iso) => {
  if (!iso) return '';
  const m = moment(iso);
  return m.isValid() ? m.format(LOCAL_FORMAT) : '';
};

const fromLocalInputValue = (val) => {
  if (!val) return null;
  const m = moment(val, LOCAL_FORMAT);
  return m.isValid() ? m.toISOString() : null;
};

const BrevoSessionDialog = ({
  isOpen,
  initialSession,
  onSave,
  onClear,
  onClose,
}) => {
  const [value, setValue] = useState('');
  const [expiresLocal, setExpiresLocal] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setValue(initialSession ? initialSession.value : '');
    setExpiresLocal(toLocalInputValue(initialSession ? initialSession.expiresAt : null));
  }, [isOpen, initialSession]);

  const expiresInPast = useMemo(() => {
    const iso = fromLocalInputValue(expiresLocal);
    if (!iso) return false;
    return new Date(iso).getTime() <= Date.now();
  }, [expiresLocal]);

  const minExpiresLocal = useMemo(() => (
    isOpen ? moment().format(LOCAL_FORMAT) : ''
  ), [isOpen]);

  const canSave = value.trim().length > 0 && !expiresInPast;
  const canClear = !!initialSession;

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      value: value.trim(),
      expiresAt: fromLocalInputValue(expiresLocal),
    });
  };

  return (
    <DialogModal open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">{t.title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>
          {t.subtitle}
        </Typography>

        <Stack spacing={2.5}>
          <Box>
            <InputLabel>{t.cookieLabel}</InputLabel>
            <StyledTextField
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={t.cookiePlaceholder}
              multiline
              minRows={3}
              maxRows={6}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t.cookieHelper}
            </Typography>
          </Box>

          <Box>
            <InputLabel>{t.expiresLabel}</InputLabel>
            <StyledTextField
              type="datetime-local"
              value={expiresLocal}
              onChange={(e) => setExpiresLocal(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: minExpiresLocal }}
              error={expiresInPast}
              sx={{ width: 240 }}
            />
            <Typography
              variant="caption"
              color={expiresInPast ? 'error' : 'text.secondary'}
              sx={{ display: 'block', mt: 0.5 }}
            >
              {expiresInPast ? t.expiresError : t.expiresHelper}
            </Typography>
          </Box>

          <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
              <Typography variant="body2" fontWeight={600}>{t.howToTitle}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box component="ol" sx={{ m: 0, pl: 3, color: 'text.secondary' }}>
                {t.howToSteps.map((step, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Typography component="li" variant="body2" key={idx} sx={{ mb: 0.5 }}>
                    {step}
                  </Typography>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>

          <Alert severity="warning" variant="outlined">
            {t.warning}
          </Alert>

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
            <Box>
              {canClear && (
                <ClearButton
                  $fitToContent
                  startIcon={<TrashIcon />}
                  onClick={onClear}
                >
                  {t.clear}
                </ClearButton>
              )}
            </Box>
            <Stack direction="row" spacing={2.5} alignItems="center">
              <CancelButton onClick={onClose}>
                {t.cancel}
              </CancelButton>
              <SaveButton disabled={!canSave} onClick={handleSave}>
                {t.save}
              </SaveButton>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </DialogModal>
  );
};

BrevoSessionDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  initialSession: PropTypes.shape({
    value: PropTypes.string,
    expiresAt: PropTypes.string,
  }),
  onSave: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

BrevoSessionDialog.defaultProps = {
  initialSession: null,
};

export default BrevoSessionDialog;
